import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NO_ERRORS_SCHEMA, NgModule, NgZone, APP_INITIALIZER } from '@angular/core';
import { Router, RouteReuseStrategy } from '@angular/router';

import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { NgIdleModule } from '@ng-idle/core';
import { TraceService } from '@sentry/angular';
import {
  DesktopDataAccessCoreServicesModule,
  RemoteOverrideLoader
} from '@brightside-web/desktop/data-access/core-services';
import { BrightsideUiModule, NgxMaskModule, ngxMaskOptions, MessageService } from '@brightside/brightside-ui';
import {
  DesktopDataAccessSharedModule,
  FirebaseAnalyticsService,
  FirebaseService,
  AmplitudeService,
  BrightsideTranslateService,
  CompanyService,
  StatusNotificationsService,
} from '@brightside-web/desktop/data-access/shared';
import { BsErrorHandler, SharedDesktopModule } from '@brightside-web/shared/desktop';
import {
  CADS_MAIN_TEMPLATE_ANALYTICS,
  CadsGlobalModalHostComponent,
  CadsGlobalToastHostComponent,
  type CadsMainTemplateAnalyticsSink,
} from '@brightside/angular-cads';
import { BrightsideUiServicesModule } from '@brightside/brightside-ui-services';
import { MicroMessageBusModule, MessageBusIncomingService } from '@brightside-web/micro/core/message-bus';
import { Environment } from '@brightside-web/micro/core/environment';

import { IntercomModule } from '@supy-io/ngx-intercom';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { VerifyComponent } from './verify/verify.component';
import { env, environment } from '../environments/environment';
import { WaitlistComponent } from './waitlist/waitlist.component';
import { WaitlistService } from './waitlist.service';
import { RoutingService } from './routing.service';
import { SharedMobileModule } from '@brightside-web/shared/mobile';

import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageSelectionModule } from '@brightside-web/desktop/feature/language-selection';

import { BsApiService, FeatureFlagService } from '@brightside-web/desktop/data-access/core-services';

import { SimpleGlobalModalComponent } from './global-modal/simple-global-modal.component';

import { DesktopFeatureFinancialAccountsCoreModule } from '@brightside-web/desktop/feature/financial-accounts-core';

import { BsHubService, BsAuthService, BsCacheService } from '@brightside-web/desktop/data-access/core-services';
import { AuthModule } from '@auth0/auth0-angular';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { MockLegalDocsInterceptor } from '@brightside-web/shared/legal-documents';
import { MarkdownModule } from 'ngx-markdown';

// BEGIN NGRX STORE
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { serviceInventoryReducer, ServiceInventoryService } from "@brightside-web/desktop/data-access/service-inventory";
import { ServiceInventoryEffects } from "@brightside-web/desktop/data-access/service-inventory";
import { homeFeedReducer } from '@brightside-web/desktop/feature/home-feed';
import {
  ServiceNotificationsEffects, ServiceNotificationsMenuComponent,
  serviceNotificationsReducer
} from '@brightside-web/desktop/feature/service-notifications';
import {
  CardOrderMockInterceptor,
  DevMenuComponent,
  TransferFundsMockInterceptor,
} from '@brightside-web/desktop/feature/dev-settings';
import { OnboardingMockInterceptor } from '@brightside-web/desktop/feature/onboarding2';
// END NGRX STORE

// Note this function is required for ngx-lottie
export function playerFactory() {
  return player;
}

export function HttpLoaderFactory(http: HttpClient) {
  const origin = window.location.origin;
  return new TranslateHttpLoader(http, `${origin}/assets/i18n/`, '.json');
}

export function CompositeLoaderFactory(
  http: HttpClient,
  analytics: FirebaseService,
): TranslateLoader {
  const origin = window.location.origin; // or just '/assets/i18n/' if you prefer
  const base = new TranslateHttpLoader(http, `${origin}/assets/i18n/`, '.json');
  return new RemoteOverrideLoader(base, analytics, env);
}

@NgModule({
  declarations: [AppComponent, VerifyComponent, WaitlistComponent, SimpleGlobalModalComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    BrightsideUiModule,
    AppRoutingModule,
    NgIdleModule.forRoot(),
    DesktopDataAccessSharedModule,
    IntercomModule.forRoot({
      appId: env.intercomAppId,
    }),
    SharedDesktopModule,
    ReactiveFormsModule,
    BrightsideUiServicesModule,
    NgxMaskModule.forRoot(ngxMaskOptions),
    SharedMobileModule,
    MicroMessageBusModule,
    HttpClientModule,
    MarkdownModule.forRoot(),

    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: CompositeLoaderFactory,
        deps: [HttpClient, FirebaseService],
      },
    }),
    LanguageSelectionModule,
    DesktopDataAccessCoreServicesModule,
    DesktopFeatureFinancialAccountsCoreModule,
    AuthModule.forRoot({
      domain: env.auth0.domain,
      clientId: env.auth0.clientId,
      authorizationParams: {
        redirect_uri: window.location.origin + '/authenticating',
        audience: env.auth0.audience,
        scope: 'openid profile email phone_number read:current_user read:current_user_metadata offline_access',
      },
      cacheLocation: env.auth0.cacheLocation as any,
      useRefreshTokens: true,
    }),
    StoreModule.forRoot({
      serviceInventory: serviceInventoryReducer,
      homeFeed: homeFeedReducer,
      serviceNotifications: serviceNotificationsReducer,
    }),
    EffectsModule.forRoot([ServiceInventoryEffects, ServiceNotificationsEffects]),
    DevMenuComponent,
    LottieComponent,
    ServiceNotificationsMenuComponent,
    CadsGlobalModalHostComponent,
    CadsGlobalToastHostComponent,
  ],
  providers: [
    BsHubService,
    BsAuthService,
    BsCacheService,
    AmplitudeService,
    { provide: Environment, useValue: environment },
    {
      provide: TraceService,
      deps: [Router],
    },
    { provide: FirebaseService, useClass: FirebaseAnalyticsService },
    {
      provide: CADS_MAIN_TEMPLATE_ANALYTICS,
      useFactory: (firebase: FirebaseService): CadsMainTemplateAnalyticsSink => ({
        logEvent: (eventName, eventParams) => firebase.logEvent(eventName, eventParams),
      }),
      deps: [FirebaseService],
    },
    { provide: ErrorHandler, useClass: BsErrorHandler },
    { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
    { provide: TranslateService, useClass: BrightsideTranslateService },
    provideLottieOptions({
      player: playerFactory,
    }),
    CurrencyPipe,
    TitleCasePipe,
    MessageService,
    WaitlistService,
    RoutingService,
    CompanyService,
    StatusNotificationsService,
    BsApiService,
    FeatureFlagService,
    ServiceInventoryService,
    { provide: HTTP_INTERCEPTORS, useClass: CardOrderMockInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TransferFundsMockInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA],
})
export class AppModule {
  constructor(
    private messageBusIncomingService: MessageBusIncomingService,
    private _ngZone: NgZone,
    private _traceService: TraceService
  ) {
    (window as any).BsCrossCom = {};
    (window as any).BsCrossCom.publishMessage = this.publishMessage.bind(this);
  }

  /**
   *
   * @param rawNativeMessage: string - Example: 'auth <tokenId>'
   */
  publishMessage(rawNativeMessage: string): void {
    this._ngZone.run(() => this.messageBusIncomingService.handleIncoming(rawNativeMessage));
  }
}


// Test items for future use
// { provide: HTTP_INTERCEPTORS, useClass: MockLegalDocsInterceptor, multi: true }
// { provide: HTTP_INTERCEPTORS, useClass: OnboardingMockInterceptor, multi: true }
